import React from "react"
import styled from "styled-components"
import AccentDivider from "../shared/accent-title-divider/accent-title-divider"
import Breakpoints from "../shared/breakpoints"
import Grid from "../shared/grid"
import { T } from "../shared/typography.styles"
import { useStaticQuery, graphql } from "gatsby"
import PartnerCards from "./partner-gallery.partner-cards"

export const PartnerTypes = Object.freeze({
  advertising: "ad",
  mobile: "mobile",
  positioning: "positioning",
  resellers: "resellers",
  iot: "iot",
})

const PartnerContent = [
  {
    logo: "ad stash",
    type: PartnerTypes.advertising,
    description:
      "AdStash offers a programmatic advertising platform for your digital signage displays. Together with Mappedin, the joint solution enables venues to monetize their digital wayfinding screens by integrating advertising. ",
  },
  {
    logo: "broadsign",
    type: PartnerTypes.advertising,
    description:
      "Broadsign is a Digital Out-of-Home solutions provider. Broadsign and Ayuda’s digital signage displays integrate seamlessly alongside Mappedin’s wayfinding on our Digital Directory.",
  },
  {
    logo: "intersection",
    type: PartnerTypes.advertising,
    description:
      "Intersection’s solutions are used in smart cities, transit systems, and other public spaces. Together, Mappedin and Intersection provide customers with interactive kiosk experiences with advertising displays, visitor information, location search, and wayfinding. ",
  },
  {
    logo: "cineplex",
    type: PartnerTypes.advertising,
    description:
      "Cineplex Digital Media is a digital experience agency that focuses on digital signage content, hardware, and strategy. Mappedin’s integration with CDM brings digital displays and interactive maps to life. ",
  },
  {
    logo: "mapbox",
    type: PartnerTypes.mobile,
    description:
      "Mappedin is MapBox compatible, allowing your development team to take advantage of their building blocks and create a custom experience. Leverage MapBox’s 3D rendering, dynamic text, and outdoor maps along with Mappedin’s powerful tools for indoor mapping.",
  },
  {
    logo: "kipsu",
    type: PartnerTypes.mobile,
    description:
      "Kipsu focuses on real-time engagement and connects brands with their customers through messaging. When navigating the indoors, users can take directions on-the-go with Mappedin and Kipsu by texting directions from the Mappedin Directory to a mobile device.",
  },
  {
    logo: "venue next",
    type: PartnerTypes.mobile,
    description:
      "VenueNext provides mobile commerce technology for indoor spaces. When integrated with our maps, users can take advantage of in-seat ordering and other enhanced experiences with VenueNext’s digital ordering platform. ",
  },
  {
    logo: "apple",
    type: PartnerTypes.positioning,
    description:
      "Mappedin leverages Apple’s free indoor positioning system to provide a blue dot experience that does not rely on hardware. Through Mappedin’s web application, users can view their precise location, getting a turn-by-turn wayfinding experience directly through the browser.  ",
  },
  {
    logo: "indoor atlas",
    type: PartnerTypes.positioning,
    description:
      "IndoorAtlas is an indoor positioning platform for mobile applications. By integrating with Mappedin’s mobile solutions, we are able to offer turn-by-turn directions as users navigate through indoor venues.   ",
  },
  {
    logo: "zenrin",
    type: PartnerTypes.resellers,
    description:
      "A leading provider of mapping software in Japan, ZENRIN sells Mappedin’s indoor mapping solutions along with developing new services for smart cities, urban development, and MaaS spaces.",
  },
  {
    logo: "informatix",
    type: PartnerTypes.resellers,
    description:
      "As one of the leading digital solutions providers in Japan, Informatix works with Mappedin to incorporate digital mapping into holistic solutions for the built environment. As a reseller, Informatix aims to integrate mapping and wayfinding into malls across Japan.",
  },
  {
    logo: "navv-systems",
    type: PartnerTypes.resellers,
    description:
      "Navv Systems provides real-time location systems for healthcare’s fast-paced and dynamic work environment. Mappedin’s indoor maps and wayfinding help Navv Systems' clients with navigating large multi-level spaces.",
  },
  {
    logo: "servicenow",
    type: PartnerTypes.iot,
    description:
      "ServiceNow’s cloud computing platform helps companies manage digital workflows for enterprise operations. Our integration helps offices manage their maps and provide accurate location data for meeting room and hot desk booking, wayfinding, and more. ",
  },
  {
    logo: "thoughtwire",
    type: PartnerTypes.iot,
    description:
      "The ThoughtWire platform offers Operations Performance Management (OPM) for the built environment. Paired with Mappedin’s accurate indoor maps, the integration allows building operators to maintain their spaces through IoT applications.",
  },
  {
    logo: "thryve ai",
    type: PartnerTypes.iot,
    description:
      "ThryveAI delivers world-class technology that helps grocers interlace their digital and physical commerce channels to improve their customer experience and capitalize on innovation. Mappedin’s digital maps help to merge this digital experience with the physical.",
  },
  {
    logo: "armored things",
    type: PartnerTypes.iot,
    description:
      "Armored Things offers real-time crowd intelligence to stadiums and campuses, creating safer and more intelligent physical spaces. Mappedin’s mapping software acts as a foundational layer for this data to help visualize movement and the visitor journey.",
  },
]

const Container = styled.div`
  padding: 48px 0;

  @media (min-width: ${Breakpoints.md}) {
    padding: 48px 0 64px;
  }
  @media (min-width: ${Breakpoints.lg}) {
    padding: 96px 0;
  }
`

const SectionTitle = styled.h3`
  ${T.sm.h3.semiBold}
  grid-column: 1 / -1;
  text-align: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #2e2e2e33;
  color: var(--tricorn-black);
  margin-bottom: 32px;
  :not(:first-child) {
    margin-top: 64px;
  }
  @media (min-width: ${Breakpoints.md}) {
    text-align: left;
    ${T.md.h3.semiBold}
    grid-column: 2 / -2;
  }
  @media (min-width: ${Breakpoints.lg}) {
    ${T.lg.h4.semiBold}
    margin-bottom: 48px;
    :not(:first-child) {
      margin-top: 96px;
    }
  }
`

export default function PartnerGallery(props) {
  const logos = useStaticQuery(graphql`
    query {
      allFile(
        filter: {
          relativeDirectory: {
            eq: "3_RESOURCES/Partner Program/01_desktop/partner logos"
          }
        }
      ) {
        nodes {
          name
          childImageSharp {
            fluid(maxWidth: 350, quality: 90) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
      }
    }
  `).allFile.nodes.reduce((acc, current) => {
    acc[current.name] = current
    return acc
  }, {})

  const sortedContent = PartnerContent.reduce((acc, current) => {
    const currentWithLogo = {
      ...current,
      childImageSharp: logos[current.logo].childImageSharp,
    }

    acc[current.type] = acc[current.type]
      ? [...acc[current.type], currentWithLogo]
      : [currentWithLogo]
    return acc
  }, {})

  return (
    <>
      <AccentDivider>Meet Our Partners</AccentDivider>
      <Container>
        <Grid>
          <SectionTitle>Advertising & Signage</SectionTitle>
          <PartnerCards cardData={sortedContent[PartnerTypes.advertising]} />
          <SectionTitle>Mobile Technology</SectionTitle>
          <PartnerCards cardData={sortedContent[PartnerTypes.mobile]} />
          <SectionTitle>Indoor Positioning</SectionTitle>
          <PartnerCards cardData={sortedContent[PartnerTypes.positioning]} />
          <SectionTitle>Resellers</SectionTitle>
          <PartnerCards cardData={sortedContent[PartnerTypes.resellers]} />
          <SectionTitle>
            Internet of Things (I
            <span style={{ textTransform: "lowercase" }}>o</span>T)
          </SectionTitle>
          <PartnerCards cardData={sortedContent[PartnerTypes.iot]} />
        </Grid>
      </Container>
    </>
  )
}
